import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import { listNews, deleteNews } from '../../services/cms';
import NewsModal from '../../components/modals/NewsModal';

const NewsTab = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    search: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    loadNews();
  }, [pagination.page, filters]);

  const loadNews = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category }),
        ...(filters.search && { search: filters.search })
      };

      const response = await listNews(params);
      if (response && response.success !== false && response.data) {
        setNews(response.data);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            pages: response.pagination.pages
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load news:', error);
      alert('Failed to load news articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;

    try {
      const response = await deleteNews(id);
      if (response && response.success !== false) {
        alert('News article deleted successfully');
        loadNews();
      } else {
        alert(response?.error || 'Failed to delete news article');
      }
    } catch (error) {
      console.error('Failed to delete news:', error);
      alert('Failed to delete news article');
    }
  };

  const handleEdit = (newsItem) => {
    setSelectedNews(newsItem);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedNews(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
    loadNews();
  };

  const getStatusBadge = (status) => {
    const badges = {
      'published': 'bg-green-100 text-green-700',
      'draft': 'bg-yellow-100 text-yellow-700',
      'archived': 'bg-gray-100 text-gray-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'Latest Updates': 'bg-blue-100 text-blue-700',
      'Product Updates': 'bg-purple-100 text-purple-700',
      'About COA Library': 'bg-indigo-100 text-indigo-700',
      'Events & Webinars': 'bg-pink-100 text-pink-700',
      'Insights & Research': 'bg-orange-100 text-orange-700'
    };
    return badges[category] || 'bg-gray-100 text-gray-700';
  };

  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          {row.featuredImage && (
            <img 
              src={row.featuredImage} 
              alt={row.title}
              className="w-12 h-12 object-cover rounded-lg"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadge(value)}`}>
          {value}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(value)}`}>
          {value}
        </span>
      )
    },
    {
      header: 'Featured',
      accessor: 'isFeatured',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      header: 'Published',
      accessor: 'publishedAt',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Not published'
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const actionMenuItems = (row) => [
    {
      label: 'Edit',
      onClick: () => handleEdit(row),
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M11.3333 2.00004C11.5084 1.82494 11.716 1.68605 11.9439 1.59129C12.1717 1.49653 12.4154 1.44775 12.6616 1.44775C12.9079 1.44775 13.1516 1.49653 13.3794 1.59129C13.6072 1.68605 13.8149 1.82494 13.99 2.00004C14.1651 2.17513 14.304 2.38283 14.3987 2.61065C14.4935 2.83847 14.5423 3.08213 14.5423 3.32837C14.5423 3.57461 14.4935 3.81827 14.3987 4.04609C14.304 4.27391 14.1651 4.48161 13.99 4.65671L5.00001 13.6467L1.33334 14.6667L2.35334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      label: 'Delete',
      onClick: () => handleDelete(row._id),
      danger: true,
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33334 13.687 3.33334 13.3333V4M5.33334 4V2.66667C5.33334 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33334C9.68696 1.33333 10.0261 1.47381 10.2762 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ];

  if (loading && news.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              <option value="">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={filters.category}
              onChange={(e) => {
                setFilters({ ...filters, category: e.target.value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              <option value="">All Categories</option>
              <option value="Latest Updates">Latest Updates</option>
              <option value="Product Updates">Product Updates</option>
              <option value="About COA Library">About COA Library</option>
              <option value="Events & Webinars">Events & Webinars</option>
              <option value="Insights & Research">Insights & Research</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search news..."
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-emerald-400 text-white rounded-lg hover:bg-emerald-500 font-medium text-sm"
        >
          + Create News
        </button>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={news}
        loading={loading}
        actionMenuItems={actionMenuItems}
        pagination={pagination}
        currentPage={pagination.page}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* News Modal */}
      {isModalOpen && (
        <NewsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          news={selectedNews}
        />
      )}
    </div>
  );
};

export default NewsTab;

